import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
	ClinicDaySnapshot,
	ConsultState,
	FLOW_STEP_ORDER,
	FlowStepId,
	PatientSource,
	QueuedPatient
} from "../../../../@types/clinicDay.types";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import { UIContext } from "../../../../contexts";
import { api } from "../../../../utils/api";
import {
	DataGrid,
	DataGridColumn,
	GridAction,
	Page,
	PageHeader,
	Pill,
	PillTone,
	SectionLabel,
	StackedCell,
	StatTile,
	StatTiles
} from "../../../shared";

type StepMeta = {
	title: string;
	owner: string;
	/** Colour family for the step's pill wherever it appears as a current step. */
	tone: PillTone;
};

const STEP_META: Record<FlowStepId, StepMeta> = {
	arrival: { title: "Arrival", owner: "Walk-in / referred", tone: "neutral" },
	registration: { title: "Registration", owner: "Self or front desk", tone: "blue" },
	doctor_consult: { title: "Doctor consult", owner: "Optional", tone: "amber" },
	scan_assessment: { title: "Scan assessment", owner: "Technician", tone: "purple" },
	additional_questions: { title: "Additional questions", owner: "Technician", tone: "amber" },
	recommendation: { title: "Recommendation", owner: "System", tone: "purple" },
	choose_product: { title: "Choose product", owner: "Patient + technician", tone: "green" },
	billing: { title: "Billing & payment", owner: "Front desk", tone: "neutral" }
};

const formatRupees = (amount: number): string => `₹ ${amount.toLocaleString("en-IN")}`;

const formatClock = (date: Date): string =>
	[date.getHours(), date.getMinutes(), date.getSeconds()].map((part) => String(part).padStart(2, "0")).join(":");

/** "P19204 · 54F · new patient", degrading gracefully while the patient registers. */
const describePatient = (patient: QueuedPatient): JSX.Element => {
	const identity = patient.patientId ?? "—";
	const demographics = patient.age === null ? null : `${patient.age}${patient.gender ?? ""}`;
	const standing = patient.patientId === null ? "registering" : patient.isRepeat ? "repeat" : "new patient";
	return (
		<>
			{[identity, demographics].filter(Boolean).join(" · ")}
			{" · "}
			{patient.isRepeat ? <strong>{standing}</strong> : standing}
		</>
	);
};

const describeSource = (source: PatientSource): { label: string; tone: PillTone } => {
	switch (source.kind) {
		case "doctor_referral":
			return { label: `${source.referrer} ref.`, tone: "blue" };
		case "franchise_referral":
			return { label: "Franchise ref.", tone: "blue" };
		default:
			return { label: "Walk-in", tone: "green" };
	}
};

const describeConsult = (consult: ConsultState): { label: string; tone: PillTone; detail: string | null } => {
	switch (consult.status) {
		case "done":
			return { label: "✓ Done", tone: "green", detail: consult.doctor };
		case "waiting":
			return { label: "Waiting", tone: "amber", detail: null };
		case "skipped":
			return { label: "Skipped", tone: "neutral", detail: null };
		default:
			return { label: "—", tone: "neutral", detail: null };
	}
};

/** The label shown in the "Current step" column, which reads more naturally than the step name. */
const describeCurrentStep = (patient: QueuedPatient): { label: string; tone: PillTone } => {
	if (patient.confirmedOrder) {
		return { label: `✓ Confirmed · ${formatRupees(patient.confirmedOrder.amount)}`, tone: "neutral" };
	}

	const meta = STEP_META[patient.currentStep];
	const qualifier = patient.stepDetail ? ` — ${patient.stepDetail}` : "";

	switch (patient.currentStep) {
		case "scan_assessment":
			return { label: `Scan in progress${qualifier}`, tone: meta.tone };
		case "doctor_consult":
			return { label: `Awaiting consult${qualifier}`, tone: meta.tone };
		case "choose_product":
			return { label: `Choosing product${qualifier}`, tone: meta.tone };
		default:
			return { label: `${meta.title}${qualifier}`, tone: meta.tone };
	}
};

const STEP_STATE_CLASS = {
	done: "border-t-brand-green bg-[#f2f7ef]",
	active: "border-t-brand-orange bg-[#fdf3dc]",
	pending: "border-t-brand-grey bg-surface"
} as const;

const TodaysClinic: FC = (): JSX.Element => {
	const { setDashboardHeader } = useContext(UIContext);
	const [snapshot, setSnapshot] = useState<ClinicDaySnapshot | null>(null);
	const [now, setNow] = useState(() => new Date());

	const loadSnapshot = useCallback(async () => {
		setSnapshot(await api.clinicDay.getClinicDay());
	}, []);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.todays_clinic);
		loadSnapshot();
	}, [setDashboardHeader, loadSnapshot]);

	useEffect(() => {
		const timer = window.setInterval(() => setNow(new Date()), 1000);
		return () => window.clearInterval(timer);
	}, []);

	/** Everything before the earliest active step reads as done, everything after as pending. */
	const firstActiveIndex = useMemo(() => {
		if (!snapshot || snapshot.activeSteps.length === 0) {
			return FLOW_STEP_ORDER.length;
		}
		return Math.min(...snapshot.activeSteps.map((step) => FLOW_STEP_ORDER.indexOf(step)));
	}, [snapshot]);

	const columns = useMemo<DataGridColumn<QueuedPatient>[]>(
		() => [
			{
				key: "token",
				header: "Token",
				width: "6rem",
				render: (patient) => <span className="font-bold tabular-nums text-ink">{patient.token}</span>
			},
			{
				key: "patient",
				header: "Patient",
				render: (patient) => <StackedCell primary={patient.name} secondary={describePatient(patient)} />
			},
			{
				key: "source",
				header: "Source",
				render: (patient) => {
					const source = describeSource(patient.source);
					return <Pill tone={source.tone}>{source.label}</Pill>;
				}
			},
			{
				key: "consult",
				header: "Consult",
				render: (patient) => {
					const consult = describeConsult(patient.consult);
					return (
						<>
							<Pill tone={consult.tone}>{consult.label}</Pill>
							{consult.detail && (
								<span className="mt-1 block text-[13px] text-muted">{consult.detail}</span>
							)}
						</>
					);
				}
			},
			{
				key: "currentStep",
				header: "Current step",
				render: (patient) => {
					const step = describeCurrentStep(patient);
					return <Pill tone={step.tone}>{step.label}</Pill>;
				}
			},
			{
				key: "attendant",
				header: "With",
				render: (patient) =>
					patient.attendant ? (
						<StackedCell primary={patient.attendant.name} secondary={patient.attendant.detail} />
					) : (
						"—"
					)
			},
			{
				key: "waitingMinutes",
				header: "Waiting",
				width: "7rem",
				render: (patient) => (
					<span className="tabular-nums text-muted">
						{patient.waitingMinutes === null ? "—" : `${patient.waitingMinutes} min`}
					</span>
				)
			},
			{
				key: "action",
				header: "Action",
				width: "9rem",
				stopRowClick: true,
				render: (patient) => (
					<GridAction
						tone={
							patient.nextAction.tone === "technician"
								? "attention"
								: patient.nextAction.tone === "ready"
								? "primary"
								: "quiet"
						}
						disabled={patient.nextAction.tone === "complete"}
					>
						{patient.nextAction.label}
						{patient.nextAction.tone !== "complete" && <span aria-hidden="true"> →</span>}
					</GridAction>
				)
			}
		],
		[]
	);

	if (!snapshot) {
		return <Page>{null}</Page>;
	}

	const { clinic, summary, queue, activeSteps } = snapshot;

	return (
		<Page>
			<PageHeader
				title="Today's Clinic"
				subtitle="Patient flow from registration to product confirmation"
				meta={[
					{ label: "Clinic", value: [clinic.name, clinic.area].filter(Boolean).join(" · ") },
					{ label: "Time", value: formatClock(now) }
				]}
			/>

			<StatTiles>
				<StatTile
					tone="blue"
					label="Registered"
					value={summary.registered.count}
					caption={`${summary.registered.walkIn} walk-in · ${summary.registered.referred} referred`}
				/>
				<StatTile
					tone="orange"
					label="Awaiting consult"
					value={summary.awaitingConsult.count}
					caption="Optional step"
				/>
				<StatTile
					tone="purple"
					label="In scanning"
					value={summary.inScanning.count}
					caption={summary.inScanning.deviceLabel ?? "—"}
				/>
				<StatTile
					tone="green"
					label="Choosing product"
					value={summary.choosingProduct.count}
					caption="Recommendation ready"
				/>
				<StatTile
					tone="grey"
					label="Confirmed today"
					value={summary.confirmedToday.count}
					caption={`${formatRupees(summary.confirmedToday.bookedAmount)} booked`}
				/>
			</StatTiles>

			<SectionLabel>Patient flow — every patient moves left to right</SectionLabel>

			<ol className="mb-6 grid list-none grid-cols-2 gap-3 p-0 md:grid-cols-4 xl:grid-cols-8">
				{FLOW_STEP_ORDER.map((step, index) => {
					const meta = STEP_META[step];
					const state = activeSteps.includes(step) ? "active" : index < firstActiveIndex ? "done" : "pending";
					return (
						<li
							key={step}
							className={`flex flex-col gap-0.5 rounded-lg border border-line border-t-[3px] px-3.5 pb-3.5 pt-2.5 ${STEP_STATE_CLASS[state]}`}
						>
							<span className="text-[10px] font-bold uppercase tracking-[0.09em] text-faint">
								Step {index + 1}
							</span>
							<span className="text-[15px] font-bold text-ink">{meta.title}</span>
							<span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
								{meta.owner}
							</span>
						</li>
					);
				})}
			</ol>

			<DataGrid
				rows={queue}
				columns={columns}
				getRowId={(patient) => patient.token}
				getRowClassName={(patient) => (patient.confirmedOrder ? "opacity-60" : undefined)}
				emptyMessage="No patients in the clinic today."
				minWidth="70rem"
			/>
		</Page>
	);
};

export default TodaysClinic;
